import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import axios from 'axios';

// Normalize to backend codes: 'en', 'am', or 'om'
const normalizeLanguage = (lng) => {
  if (!lng || typeof lng !== 'string') return 'en';
  const lower = lng.trim().toLowerCase();
  if (lower.startsWith('am') || lower === 'amharic') return 'am';
  if (lower.startsWith('om') || lower === 'oromo') return 'om';
  return 'en'; // en, en-US, en-GB, etc.
};

const loadMessages = async (lng) => {
  const normalized = normalizeLanguage(lng);
  try {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
    const response = await axios.get(`${apiUrl}/localization/messages?language=${normalized}`);
    // API returns { success: true, data: { ...messages... }, ... }
    const raw = response.data;
    const messages = (raw && typeof raw === 'object' && raw.data && typeof raw.data === 'object')
      ? raw.data
      : (raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {});
    const count = Object.keys(messages).length;
    console.log(`Loaded ${count} translations for language: ${normalized}`);
    return { messages, normalized, count };
  } catch (error) {
    console.error('Error loading translations:', error);
    return { messages: {}, normalized, count: 0 };
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    debug: false,
    interpolation: {
      escapeValue: false,
    },
    resources: {},
    react: {
      useSuspense: false,
    },
    // Better handling of missing keys
    returnEmptyString: false,
    returnNull: false,
    // Don't show the key if translation is missing - show a placeholder
    parseMissingKeyHandler: (key) => {
      console.warn(`⚠️ Missing translation key: ${key}`);
      missingKeysCollector.add(key);
      // Return a more user-friendly message instead of the raw key
      return key.split('.').pop().replace(/([A-Z])/g, ' $1').trim() || key;
    },
    // Save missing keys for debugging
    saveMissing: false,
  });

// Collect missing keys in dev so you can detect untranslated keys at runtime
const missingKeysCollector = {
  _keys: new Set(),
  add(key) {
    this._keys.add(key);
    if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
      window.__missingI18nKeys = this.getAll();
    }
  },
  getAll() {
    return Array.from(this._keys).sort();
  },
  clear() {
    this._keys.clear();
    if (typeof window !== 'undefined') window.__missingI18nKeys = [];
  },
};

if (typeof window !== 'undefined') {
  window.getMissingTranslationKeys = () => missingKeysCollector.getAll();
  window.clearMissingTranslationKeys = () => missingKeysCollector.clear();
  window.__missingI18nKeys = [];
}

// Load messages dynamically when language changes
i18n.on('languageChanged', async (lng) => {
  try {
    const { messages, normalized, count } = await loadMessages(lng);
    if (count > 0) {
      i18n.addResourceBundle(normalized, 'translation', messages, true, true);
      window.dispatchEvent(new Event('i18n-loaded'));
    }
  } catch (error) {
    console.error('Error loading translations on language change:', error);
  }
});

// Load initial language in the background (do not block app render)
const initializeI18n = async () => {
  const lng = i18n.language || 'en';
  const normalized = normalizeLanguage(lng);
  try {
    const { messages, count } = await loadMessages(lng);
    if (count > 0) {
      i18n.removeResourceBundle(normalized, 'translation');
      i18n.addResourceBundle(normalized, 'translation', messages, true, true);
      if (i18n.language !== normalized) {
        i18n.changeLanguage(normalized);
      }
      window.dispatchEvent(new Event('i18n-loaded'));
      console.log('✓ Translations loaded:', count, 'keys for', normalized);
    }
  } catch (e) {
    console.warn('Translations load failed (app will show fallbacks):', e?.message);
  }
};

// Start loading translations without blocking – app can render immediately
initializeI18n();

// Export function to reload translations (useful after backend updates)
export const reloadTranslations = async (language = null) => {
  try {
    const lng = language || i18n.language || 'en';
    const { messages, normalized, count } = await loadMessages(lng);
    if (count > 0) {
      i18n.removeResourceBundle(normalized, 'translation');
      i18n.addResourceBundle(normalized, 'translation', messages, true, true);
      if (i18n.language !== normalized) i18n.changeLanguage(normalized);
      window.dispatchEvent(new Event('i18n-loaded'));
      console.log(`✓ Reloaded ${count} translations for ${normalized}`);
      return true;
    }
    console.warn('⚠️ No translations loaded during reload');
    return false;
  } catch (error) {
    console.error('Failed to reload translations:', error);
    return false;
  }
};

// Make reload function available globally for debugging
if (typeof window !== 'undefined') {
  window.reloadTranslations = reloadTranslations;
}

export default i18n;

